



































import {
  ref,
  computed,
  useContext,
  useRoute,
  defineComponent,
  useFetch,
  onMounted,
  watch,
} from "@nuxtjs/composition-api";
import { merge } from "lodash-es";
import { useCache, CacheTagPrefix } from "@vue-storefront/cache";
import { getName } from "~/modules/catalog/product/getters/productGetters";
import { getMetaInfo } from "~/helpers/getMetaInfo";
import { usePageStore } from "~/stores/page";
import { ProductTypeEnum } from "~/modules/catalog/product/enums/ProductTypeEnum";
import { useSearchProducts, useUiHelpers } from "~/composables";
import type { Product } from "~/modules/catalog/product/types";
import ProductSkeleton from "~/modules/catalog/product/components/ProductSkeleton.vue";
import ProductService from "~/modules/catalog/product/components/ProductService.vue";
import ProductDescription from "~/modules/catalog/product/components/ProductDescription.vue";
import ProductDetail from "~/modules/catalog/product/components/ProductDetail.vue";
import ProductBreadcrumbs from "~/modules/catalog/product/components/ProductBreadcrumbs.vue";
import { useUser } from "~/modules/customer/composables/useUser";
import LazyComponent from "v-lazy-component/vue2";
import HeaderProductDetailMobile from "~/components/Header/HeaderProductDetailMobile.vue";
import { getConfigurableOptionsByProduct } from "~/modules/catalog/product/helpers";
import { useClevertapStore } from "~/stores/clevertap";
import { useEcommercePixel } from "~/stores/ecommercePixel"; // Add the import for ecommerce pixel store
import { decodeUid } from "~/helpers";
import ProductLoadItem from "~/components/SkeletonLoader/ProductLoadItem.vue";
import { getPrices } from "~/modules/catalog/product/getters/productGetters";
import { useCategoryStore } from "~/modules/catalog/category/stores/category";
import { useTraverseCategory } from "~/modules/catalog/category/helpers/useTraverseCategory";

export default defineComponent({
  name: "ProductPage",
  components: {
    ProductSkeleton,
    LazyComponent,
    HeaderProductDetailMobile,
    ProductLoadItem,
    RecentlyViewedProducts: () =>
      import("~/modules/catalog/product/components/RecentlyViewedProducts.vue"),
    RelatedProducts: () =>
      import("~/modules/catalog/product/components/RelatedProducts.vue"),
    ProductService,
    ProductDescription,
    ProductDetail,
    ProductBreadcrumbs,
  },
  transition: "fade",
  setup() {
    const { routeData } = usePageStore();
    const product = ref<Product | null>(null);
    const { addTags } = useCache();
    const { localePath } = useContext();
    const { error: nuxtError } = useContext();
    const breadcrumbs = ref([]);
    const clevertapStore = useClevertapStore();
    const ecommercePixelStore = useEcommercePixel();
    const { fetchProducts } = useSearchProducts();
    const { user, isAuthenticated } = useUser(); // Make sure we have user data for FB tracking
    const categoryStore = useCategoryStore();
    const categoryTree = computed(() => categoryStore.categories);
    const { activeCategoryById, categoryAncestorsById } = useTraverseCategory();
    const { getCatLink } = useUiHelpers();

    const breadcrumbCategorys = (categoryIds) => {
      if (!categoryTree.value) return null;

      const categories = categoryIds
        .map((id) => activeCategoryById(id))
        .filter(
          (category) => category !== null && category?.include_in_menu == 1
        );

      if (categories.length === 0) return null;

      // Tìm category có level cao nhất
      return categories.reduce((highest, current) => {
        return (current.level || 0) > (highest.level || 0) ? current : highest;
      });
    };

    // Add a flag to track if product view has been tracked
    const hasTrackedProductView = ref(false);

    const updateBreadcrumbs = async () => {
      if (!product.value) return;

      const categoryIds = product.value?.category_ids || [];

      try {
        breadcrumbs.value = categoryAncestorsById(
          breadcrumbCategorys(categoryIds)
        )?.map((category) => ({
          text: category.name,
          link: localePath(getCatLink(category)),
        }));

        const hasHomePage = breadcrumbs.value.some(
          (breadcrumb) => breadcrumb.link === localePath("/")
        );
        if (!hasHomePage) {
          breadcrumbs.value = [
            { link: localePath("/"), text: "Trang chủ" },
            ...breadcrumbs.value,
          ];
        }
        const productName = getName(product.value);
        breadcrumbs.value.push({
          text: productName,
          link: `/${product.value?.url_path || product.value?.url_key}`,
        });
      } catch (error) {
        console.error("Error fetching breadcrumbs:", error);
        breadcrumbs.value = [{ text: "Trang chủ", link: localePath("/") }];
      }
    };

    const selectedOption = ref({
      size: null,
      color: null,
    });

    const dataSelectedOption = (dataOptions) => {
      selectedOption.value.size = dataOptions?.value?.size ?? null;
      selectedOption.value.color = dataOptions?.value?.color ?? null;
    };

    // eslint-disable-next-line no-underscore-dangle
    const renderer = computed(
      () => product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT
    );

    const productDescription = computed(
      () => product.value?.description_map || ""
    );

    const availableOptions = computed(() => {
      return getConfigurableOptionsByProduct(
        product.value?.configurable_options_map
      );
    });

    const dataCheckOptionInstock = ref([]);
    const showColorAlert = ref(false);
    const showSizeAlert = ref(false);

    const trackProductView = () => {
      if (!product.value || hasTrackedProductView.value) {
        return;
      }

      // Get from CleverTap store to check if the product was viewed from a recommendation
      const fromToData = clevertapStore.getFromtTo;
      const infoFromSearch = clevertapStore.getInforFromSearch;

      try {
        const categoriesArray = product.value.categories || [];
        const lastCategory =
          categoriesArray.length > 0
            ? categoriesArray[categoriesArray.length - 1]
            : null;

        // Build category data for tracking
        const categoryData = {};
        if (lastCategory) {
          categoryData[`category_id_level_${lastCategory.level || 1}`] =
            decodeUid(lastCategory.uid);
          categoryData[`category_name_level_${lastCategory.level || 1}`] =
            lastCategory.name;
        }

        // Get domain from current route
        const origin =
          typeof window !== "undefined" ? window.location.origin : "";

        // Get price info using the same helpers as ProductInfo component
        const regularPrice = getPrices(product.value)?.regular;
        const finalPrice = getPrices(product.value)?.final;

        // Don't proceed with tracking if we don't have valid price data
        if (!regularPrice && !finalPrice) {
          setTimeout(() => {
            hasTrackedProductView.value = false; // Reset to try again
            trackProductView();
          }, 1000);
          return;
        }

        // Prepare tracking data
        const trackingData = {
          product_id: product.value.sku,
          product_title: product.value.name,
          ...categoryData,
          product_currency: "VND",
          product_price: regularPrice || 0,
          product_quantity: 1,
          category_path: product.value.url_key,
          product_url: `${origin}/${product.value.url_key}`,
          product_image_url: product.value.image?.url || "",
          product_discount_price: finalPrice || regularPrice || 0,
          variant_count: product.value.variants?.length || 0,
          is_search_result: infoFromSearch?.is_search_result || false,
          search_result_query: infoFromSearch?.seach_result_query || "",
          list_id: lastCategory ? decodeUid(lastCategory.uid) || "" : "",
          list_name: lastCategory ? lastCategory.name || "" : "",
          position: infoFromSearch?.position || -1,
          // Initialize recommendation data properties
          from_location: "",
          from_product_id: "",
          suggest_product: false,
          suggest_product_title: "",
          block_position: "",
          suggest_product_number: "",
        };

        // Update recommendation data if product was viewed from a recommendation
        if (fromToData) {
          trackingData.from_location = fromToData.from_location || "";
          trackingData.from_product_id = fromToData.from_product_id || "";
          trackingData.suggest_product = fromToData.suggest_product || false;
          trackingData.suggest_product_title =
            fromToData.suggest_product_title || "";
          trackingData.block_position = fromToData.block_position || "";
          trackingData.suggest_product_number =
            fromToData.suggest_product_number || "";
        }

        // Send event to CleverTap
        const win = window as any;

        if (typeof win !== "undefined" && win.clevertap) {
          // Check if this method exists
          if (typeof clevertapStore.productViewdCleverTap === "function") {
            clevertapStore.productViewdCleverTap({
              clevertap: win.clevertap,
              data: trackingData,
            });
          }

          // Add Facebook Pixel tracking
          if (typeof win.fbq !== "undefined") {
            ecommercePixelStore.viewProductFbq({
              analytics: win.fbq,
              product: {
                parentSku: product.value.sku,
                sku: product.value.sku,
                name: product.value.name,
                original_price: regularPrice || 0,
                final_price: finalPrice || regularPrice || 0,
                description: product.value.description_map || "",
                url_path: product.value.url_key || "",
                image:
                  product.value.media_gallery &&
                  product.value.media_gallery.length > 0
                    ? product.value.media_gallery.map((img) => ({
                        image: img.image,
                      }))
                    : product.value.image_map
                    ? [
                        {
                          image:
                            process.env.IMG_BASE_URL +
                            "/catalog/product" +
                            product.value.image_map,
                        },
                      ]
                    : [],
              },
              userData: isAuthenticated.value && user.value ? user.value : null,
            });
          }

          // Add Google Tag Manager tracking
          if (typeof win.dataLayer !== "undefined") {
            ecommercePixelStore.viewProductGtag({
              dataLayer: win.dataLayer,
              window_dataLayer: win.dataLayer,
              product: {
                parentSku: product.value.sku,
                sku: product.value.sku,
                name: product.value.name,
                original_price: regularPrice || 0,
                final_price: finalPrice || regularPrice || 0,
                description: product.value.description_map || "",
                url_path: product.value.url_key || "",
                image: product.value.image_map
                  ? [
                      {
                        image:
                          process.env.IMG_BASE_URL +
                          "/catalog/product" +
                          product.value.image_map,
                      },
                    ]
                  : [],
              },
            });

            // Add Google Analytics 4 tracking
            ecommercePixelStore.viewProductGa4({
              dataLayer: win.dataLayer,
              window_dataLayer: win.dataLayer,
              product: {
                parentSku: product.value.sku,
                sku: product.value.sku,
                name: product.value.name,
                original_price: regularPrice || 0,
                final_price: finalPrice || regularPrice || 0,
                description: product.value.description_map || "",
                url_path: product.value.url_key || "",
              },
            });
          }

          // Add TikTok Pixel tracking
          if (typeof win.ttq !== "undefined") {
            ecommercePixelStore.viewProductTiktok({
              ttq: win.ttq,
              product: {
                parentSku: product.value.sku,
                sku: product.value.sku,
                name: product.value.name,
                original_price: regularPrice || 0,
                final_price: finalPrice || 0,
              },
            });
          }

          hasTrackedProductView.value = true;
        } else {
          console.warn("CleverTap not available on window");
        }
      } catch (error) {
        console.error("Error tracking product view:", error);
      }
    };

    const loading = ref(false);
    const { fetch } = useFetch(async () => {
      loading.value = true;
      const params = {
        skus: [routeData.sku],
      };
      const { data } = await fetchProducts(params);

      const productsData = data || [];
      if (productsData.length > 0) {
        product.value = merge(
          {},
          product.value,
          (productsData[0] as Product) || null
        );
        // Update breadcrumbs after product data is loaded
        await updateBreadcrumbs();
      }
      loading.value = false;

      if (Boolean(product?.value?.sku) === false)
        nuxtError({ statusCode: 404 });

      const tags = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${routeData.sku}`,
        },
      ];

      const productTags = [
        {
          prefix: CacheTagPrefix.Product,
          value: product.value?.uid,
        },
      ];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      addTags([...tags, ...productTags]);
    });

    onMounted(async () => {
      await fetch();
      // Track product view after data is loaded with a small delay
      // to ensure price data is loaded
      if (product.value) {
        setTimeout(() => {
          trackProductView();
        }, 500);
      }
    });

    // Watch for product changes to update breadcrumbs
    watch(
      () => product.value?.sku,
      async (newSku, oldSku) => {
        if (newSku && newSku !== oldSku) {
          // Reset tracking flag when product changes
          hasTrackedProductView.value = false;

          if (product.value) {
            await updateBreadcrumbs();
            // Track product view when product changes
            trackProductView();
          }
        }
      }
    );

    return {
      renderer,
      breadcrumbs,
      product,
      productDescription,
      isAuthenticated,
      availableOptions,
      dataCheckOptionInstock,
      showColorAlert,
      showSizeAlert,
      selectedOption,
      dataSelectedOption,
      trackProductView,
      loading,
    };
  },
  head() {
    return getMetaInfo(this.product);
  },
});
